import {
  blueDrug,
  blueStat,
  greenStat,
  greenWallet,
  lightGreenStat,
  orangeDrug,
  purpleStat,
  redDrug,
} from "@/common/components/icons/svg-icons";
import {
  IAlertNotification,
  IAllDrugData,
  IDRFStats,
  IHospitalHeadData,
  ILineChartData,
  ISalesPerformance,
  IStatisticCard,
} from "@/common/types/others";
import { INofication } from "./inventory/notifications";
import { monthMapper } from "@/common/utils/general";
import { IAuthUser } from "@/common/types/user";

export const pharmacyHeadFinancialCards = [
  {
    id: 1,
    name: "Total Stock",
    value: 20000,
    icon: greenWallet,
    color: "#43A047",
  },
  {
    id: 2,
    name: "Low Stock Drug",
    value: 3,
    icon: orangeDrug,
    color: "#FF9800",
  },
  {
    id: 3,
    name: "Total Drug",
    value: 10,
    icon: blueDrug,
    color: "#2196F3",
  },
  {
    id: 4,
    name: "Expired Drug",
    value: 3,
    icon: redDrug,
    color: "#F44336",
  },
];

export const salesStatisticCards = (data: IDRFStats) => [
  {
    id: 1,
    title: "Total Stock Value",
    value: data?.total_stock_value,
    icon: purpleStat,
    color: "#28105B",
  },
  {
    id: 2,
    title: "Revenue Generated",
    value: data?.revenue_generated,
    icon: blueStat,
    color: "#13A7F6",
  },
  {
    id: 3,
    title: "Fund Allocated",
    value: data?.funds_allocated,
    icon: lightGreenStat,
    color: "#4EE437",
  },
  {
    id: 4,
    title: "Current DRF Balance",
    value: data?.drf_total_balance,
    icon: greenStat,
    color: "#0B7F68",
  },
];

export const inventoryStatisticCards = (data: IStatisticCard) => [
  {
    id: 1,
    name: "Total Stock Value",
    value: data?.total_stock_value,
    icon: greenWallet,
    color: "#28105B",
  },
  {
    id: 2,
    name: "Low Stock Drugs",
    value: data?.low_stock_drug_count,
    icon: orangeDrug,
    color: "#DBBE2C",
  },
  {
    id: 3,
    name: "Total Drugs",
    value: data?.total_drugs,
    icon: redDrug,
    color: "#E53935",
  },
  {
    id: 4,
    name: "Expiring Soon",
    value: data?.expiring_soon_count,
    icon: blueDrug,
    color: "#C8D0FF",
  },
];

export const linechartData = (data: ILineChartData) => {
  const stock_in_dataset = Object.values(data?.stock_in || {});
  const stock_out_dataset = Object.values(data?.stock_out || {});
  return {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Stock in",
        data: stock_in_dataset,

        borderColor: "rgba(75,192,192,1)",
        borderWidth: 2,
        tension: 0.4,

        pointBackgroundColor: "white",
        pointBorderColor: "rgba(75,192,192,1)",
        pointRadius: 4,
        pointHoverRadius: 6,

        fill: true,
        backgroundColor: "rgba(75,192,192,0.2)",
      },
      {
        label: "Stock out",
        data: stock_out_dataset,
        borderColor: "rgba(255,99,132,1)",
        backgroundColor: "rgba(255,99,132,0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };
};

export const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
  scales: {
    x: {
      title: {
        text: "Month",
      },
    },
    y: {
      beginAtZero: true,
      max: 1500,
      ticks: {
        stepSize: 300,
        autoSkip: false,
        callback: (value: any) => `${value}`,
      },
      title: {
        text: "Amount in Naira",
      },
    },
  },
};

export const getLineGraphData = (data: IAllDrugData) => {
  const result = {
    total_inventory_value: data?.quantity_available ?? 0,
    inventory_items: [
      {
        product_brand: data?.product_name ?? "Unknown",
        status: data?.status ?? "low",
        total_quantity_in_stock: data?.quantity_available ?? 0,
        reorder_level: data?.value ?? 0,
      },
    ],
  };

  return result;
};

export const newFundRequestForm = (data: IAuthUser) => [
  {
    inputType: "label",
    label: "Requesting Officer Details",
  },
  {
    inputType: "text",
    name: "firstName",
    prompt: "First Name",
    rules: { required: true },
    readOnly: true,
    initialValue: data?.first_name,
    isFullWidth: false,
    required: true,
  },
  {
    inputType: "text",
    name: "lastname",
    prompt: " Last Name",
    readOnly: true,
    initialValue: data?.last_name,
    required: true,
    isFullWidth: false,
  },
  {
    inputType: "text",
    name: "email",
    prompt: "Email",
    initialValue: data?.email,
    readOnly: true,
    rules: { required: true },
    isFullWidth: false,
    required: true,
  },
  {
    inputType: "text",
    name: "number",
    prompt: "Phone Number",
    readOnly: true,
    initialValue: data?.company?.phone_number,
    required: true,
    isFullWidth: false,
  },
  {
    inputType: "label",
    label: "Request Information",
  },
  {
    inputType: "text",
    name: "request_amount",
    prompt: "Request Amount",
    placeholder: "120,000",
    required: true,
    isFullWidth: true,
  },
  {
    inputType: "textarea",
    name: "justification",
    prompt: "Purpose of request",
    placeholder: "make it worth it",
    required: true,
    isFullWidth: true,
  },
];

export const fundAllocationRequestTableHeaders = [
  "Request Date",
  "Submitted By",
  "Amount Requested",
  "Status",
];

export const fundAllocationRequestTableKeys = [
  "request_date",
  "submitted_by",
  "amount_requested",
  "status",
];

export const getFundAllocationRequestTableData = (data: any) => [
  {
    id: "1",
    request_date: { value: "2025-07-01", type: "string" },
    submitted_by: { value: "Jane Doe", type: "string" },
    amount_requested: { value: "1200", type: "string" },
    status: { value: "accepted", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
    data: data, // to be remove during integration
  },
  {
    id: "2",
    request_date: { value: "2025-06-28", type: "string" },
    submitted_by: { value: "John Smith", type: "string" },
    amount_requested: { value: "450", type: "string" },
    status: { value: "pending", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "3",
    request_date: { value: "2025-06-25", type: "string" },
    submitted_by: { value: "Amaka Okafor", type: "string" },
    amount_requested: { value: "870", type: "string" },
    status: { value: "rejected", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "4",
    request_date: { value: "2025-06-20", type: "string" },
    submitted_by: { value: "Chinedu Eze", type: "string" },
    amount_requested: { value: "1500", type: "string" },
    status: { value: "accepted", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "5",
    request_date: { value: "2025-06-18", type: "string" },
    submitted_by: { value: "Fatima Bello", type: "string" },
    amount_requested: { value: "675", type: "string" },
    status: { value: "pending", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "6",
    request_date: { value: "2025-06-15", type: "string" },
    submitted_by: { value: "Samuel Johnson", type: "string" },
    amount_requested: { value: "920", type: "string" },
    status: { value: "rejected", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "7",
    request_date: { value: "2025-06-12", type: "string" },
    submitted_by: { value: "Ngozi Nwosu", type: "string" },
    amount_requested: { value: "300", type: "string" },
    status: { value: "pending", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "8",
    request_date: { value: "2025-06-10", type: "string" },
    submitted_by: { value: "Tunde Bakare", type: "string" },
    amount_requested: { value: "1100", type: "string" },
    status: { value: "accepted", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "9",
    request_date: { value: "2025-06-07", type: "string" },
    submitted_by: { value: "Aisha Garba", type: "string" },
    amount_requested: { value: "760", type: "string" },
    status: { value: "pending", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
  {
    id: "10",
    request_date: { value: "2025-06-05", type: "string" },
    submitted_by: { value: "Babatunde Salami", type: "string" },
    amount_requested: { value: "1340", type: "string" },
    status: { value: "rejected", type: "string" },
    justification: {
      value:
        "The requested funds are intended for the immediate replenishment of essential medicines that are currently low in stock, particularly antimalarials and antibiotics, which are critical for addressing the ongoing spike in outpatient cases related to seasonal malaria and bacterial infections. Based on the April consumption data and anticipated patient load, the current inventory will not meet demand for the next 3 weeks. This allocation will ensure uninterrupted service delivery, prevent stockouts, and maintain adherence to the DRF policy on continuous availability of life-saving medications.",
      type: "string",
    },
  },
];

export const salesTableHeaders = [
  "Month",
  "Sales revenue",
  "Restocking Cost",
  "Net Balance",
];

export const salesTableKeys = [
  "month",
  "sales_revenue",
  "restocking_cost",
  "net_balance",
];

export const getSalesTableData = (data: ISalesPerformance[]) => {
  if (!data || data.length === 0) return [];

  const result = data?.map((dataItem: ISalesPerformance) => ({
    month: {
      value: monthMapper[Number(dataItem?.month)],
      type: "string",
    },
    net_balance: { value: dataItem?.net_balance, type: "string" },
    restocking_cost: { value: dataItem?.restocking_cost, type: "string" },
    sales_revenue: { value: dataItem?.sales_revenue, type: "string" },
  }));
  return result;
};

export const headOfHospitalInventoryTableHeaders = [
  "Transaction ID",
  "Date & Time",
  "Description",
  "Amount",
  "Status",
];

export const headOfHospitalInventoryTableKeys = [
  "transaction_id",
  "created_at",
  "description",
  "amount",
  "status",
];

export const getHeadOfHospitalInventoryTableData = (
  data: IHospitalHeadData[]
) => {
  if (!data || data.length === 0) return [];

  const result = data.map((dataItem: IHospitalHeadData) => ({
    transaction_id: { value: dataItem?.transaction_id, type: "string" },
    created_at: { value: dataItem?.created_at, type: "string" },
    description: { value: dataItem?.description, type: "string" },
    amount: { value: dataItem?.amount, type: "string" },
    status: { value: dataItem?.status, type: "string" },
  }));

  return result;
};

export const headOfHospitalAllDrugTableHeaders = [
  "Product Name",
  "Item ID",
  "Current Stock",
  "Value",
  "Expiry Date",
  "Status",
];

export const headOfHospitalAllDrugTableKeys = [
  "product_name",
  "item_id",
  "current_stock",
  "value",
  "expiry_date",
  "status",
];

export const getHeadOfHospitalAllDrugTableData = (data: IAllDrugData[]) => {
  if (data?.length === 0 || data === undefined) return [];
  const result = data?.map((dataItem: IAllDrugData) => ({
    id: dataItem?.id,
    product_name: {
      value: dataItem?.product_name ?? "unknown",
      type: "string",
    },
    item_id: { value: dataItem?.item_id ?? "unknown", type: "string" },
    current_stock: {
      value: String(dataItem?.quantity_available) ?? "unknown",
      type: "string",
    },
    value: { value: dataItem?.value ?? "0", type: "string" },
    expiry_date: {
      value: dataItem?.expiry_date ?? "unknown",
      type: "string",
    },
    status: { value: dataItem?.status ?? "unknown", type: "string" },
  }));
  return result;
};

export const getNotification = (data: IAlertNotification[]): INofication[] => {
  if (data?.length === 0) return [];
  return data.map((dataItem: IAlertNotification) => ({
    id: dataItem.id,
    description: dataItem.message,
    drug_name: dataItem.drug_name,
    batch_number: dataItem.batch_number,
    is_resolved: dataItem.is_resolved,
    current_stock: dataItem.current_stock,
  }));
};

// ============= All Drugs Inventory ==============
export const stockLevelInventoryTableHead = [
  "Product Name",
  "Item ID",
  "Current Stock",
  "Value",
  "Expiry Date",
];

export const restockingLogTableHead = [
  "Product Name",
  "Item ID",
  "Restock Date",
  "Qty Added",
  "Unit Price(N)",
  "Supplier",
  "Logged By",
];

export const salesLogTableHead = [
  "Product Name",
  "Item ID",
  "Invoice ID",
  "Date of Sale",
  "Qty Sold",
  "Unit Price",
  "Total Revenue",
  "Sold By",
];
